import { useEffect, useState } from 'react';
import type { ActiveSession } from '../shared/types';
import { statusLabels } from '../shared/progress';
import { api } from './api';
import { Card, ErrorNotice, formatDate, Spinner } from './components';
import { SessionRecord } from './RecordEditor';

type Day = { date: string; scheduled: boolean; projectedTemplate?: {id:string;name:string;code:string;occurrenceVersion:number}|null; sessions: { id: string; name: string; code: string; status: keyof typeof statusLabels;
  sessionStatus: string; progress: { completed: number; prescribed: number; percentage: number } }[] };
const dateInZone = (zone: string) => new Intl.DateTimeFormat('en-CA', {timeZone:zone,year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());

export function Calendar({ profileId, timezone, onWorkout, refresh = 0 }: {
  profileId: string; timezone: string; onWorkout: (session: ActiveSession) => void; refresh?: number;
}) {
  const today = dateInZone(timezone);
  const [month, setMonth] = useState(today.slice(0,7));
  const [selected, setSelected] = useState(today);
  const [days, setDays] = useState<Day[] | null>(null);
  const [error, setError] = useState('');
  const [record, setRecord] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const year = Number(month.slice(0,4)), monthIndex = Number(month.slice(5))-1;
  const count = new Date(Date.UTC(year,monthIndex+1,0)).getUTCDate();
  useEffect(() => {
    let current = true; setDays(null); setError('');
    api<Day[]>(`/calendar?from=${month}-01&to=${month}-${count}`).then(data => { if(current) setDays(data); }).catch(e => {if(current) setError(e.message);});
    return () => { current = false; };
  }, [month, profileId, refresh, revision]);
  function move(delta: number) {
    const next = new Date(Date.UTC(year,monthIndex+delta,1)).toISOString().slice(0,7);
    setMonth(next); setSelected(`${next}-01`);
  }
  const day = days?.find(d => d.date === selected);
  const offset = (new Date(`${month}-01T12:00:00Z`).getUTCDay()+6)%7;
  return <Card className="calendar">
    <div className="calendar-heading"><h2>Calendar</h2><button className="text-button" onClick={() => {setMonth(today.slice(0,7));setSelected(today);}}>Today</button></div>
    <div className="calendar-month"><button aria-label="Previous month" onClick={() => move(-1)}>‹</button>
      <label><span className="sr-only">Calendar month</span><input aria-label="Calendar month" type="month" value={month} onChange={e => { if(/^\d{4}-\d{2}$/.test(e.target.value)){setMonth(e.target.value);setSelected(`${e.target.value}-01`);} }}/></label>
      <button aria-label="Next month" onClick={() => move(1)}>›</button></div>
    <ul className="calendar-legend">{Object.entries(statusLabels).map(([key,label]) => <li key={key}><span className={`calendar-dot status-${key}`}/>{label}</li>)}</ul>
    {error && <ErrorNotice message={error}/>}
    {!days && !error ? <Spinner label="Loading calendar"/> : <>
      <div className="calendar-grid" aria-label="Workout dates">
        {['M','T','W','T','F','S','S'].map((d,i)=><span key={i} className="calendar-weekday" aria-hidden="true">{d}</span>)}
        {Array.from({length:offset},(_,i)=><span key={`empty-${i}`}/>)}
        {days?.map(d => {
          const statuses = [...new Set(d.sessions.map(s=>s.status))];
          if(!statuses.length && d.scheduled) statuses.push('not-started');
          return <button key={d.date} className={`calendar-day ${selected === d.date ? 'is-selected' : ''} ${d.date === today ? 'is-today' : ''}`}
            onClick={()=>setSelected(d.date)} aria-pressed={selected === d.date}
            aria-label={`${formatDate(`${d.date}T12:00:00`)}, ${statuses.map(s=>statusLabels[s]).join(', ') || 'No workout scheduled'}`}>
            <span>{Number(d.date.slice(-2))}</span><span className="calendar-dots">{statuses.map(s=><span key={s} className={`calendar-dot status-${s}`}/>)}</span>
          </button>;
        })}
      </div>
      <section className="calendar-day-detail" aria-label="Selected calendar day">
        <h3>{formatDate(`${selected}T12:00:00`)}</h3>
        {day?.sessions.map(session => <button key={session.id} className={`calendar-session status-${session.status}`} onClick={()=>setRecord(session.id)}>
          <strong>{session.name}</strong><span>{statusLabels[session.status]} · {session.progress.percentage}%</span><small>{session.progress.completed}/{session.progress.prescribed} sets · View / edit ›</small>
        </button>)}
        {day?.projectedTemplate && <p><strong>{day.projectedTemplate.name}</strong> · Not started<br/><span className="muted">Projected next workout. Previewing this date does not start or skip a workout.</span></p>}
        {!day?.sessions.length && !day?.projectedTemplate && <p className="muted">{day?.scheduled ? 'Not started · scheduled workout day' : 'No workout scheduled. You can still start or resume a workout on this day.'}</p>}
      </section>
    </>}
    {record && <SessionRecord id={record} profileId={profileId} onClose={()=>setRecord(null)} onChanged={()=>setRevision(v=>v+1)} onResume={onWorkout}/>}
  </Card>;
}
